import { FileDownload } from '@mui/icons-material';
import DevicesIcon from '@mui/icons-material/Devices';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Autocomplete,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
  Button,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchEthernetConnections, killEthernetConnection } from '@sophocles/api/ethernet';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import GridStatusCell from '@sophocles/data-grids/components/cells/GridStatusCell';
import DataGridToolbar from '@sophocles/data-grids/components/toolbar';

const REFRESH_OPTIONS = [
  { label: '5s', value: 5000 },
  { label: '10s', value: 10000 },
  { label: '30s', value: 30000 },
];

function getRadioCountForIdentifier(row) {
  const numericCandidates = [
    row?.duplicate_radio_count,
    row?.radio_count,
    row?.duplicate_count,
    row?.matching_radio_count,
    row?.relays_count,
  ];
  const numericValue = numericCandidates.find(value => Number.isFinite(value));
  if (Number.isFinite(numericValue)) return numericValue;

  const arrayCandidates = [row?.matching_radios, row?.duplicate_radios, row?.radios, row?.matching_relays];
  const arrayValue = arrayCandidates.find(value => Array.isArray(value));
  if (Array.isArray(arrayValue)) return arrayValue.length;

  return null;
}

function getMatchingRadios(row) {
  const arrayCandidates = [row?.matching_radios, row?.duplicate_radios, row?.radios, row?.matching_relays];
  const radios = arrayCandidates.find(value => Array.isArray(value));
  if (!Array.isArray(radios)) return [];
  return radios
    .map(item => {
      if (item && typeof item === 'object') {
        return {
          id: item.id,
          label: item.iccid || item.mac_address || String(item.id || ''),
          siteName: item.site_name || '',
          siteUrl: item.site_url || '',
        };
      }
      return null;
    })
    .filter(item => item && item.id);
}

function SummaryCard({ card, active = false, loading = false, onClick }) {
  const theme = useTheme();
  const IconComponent = card.icon;
  const cardContent = (
    <CardContent
      sx={{
        p: 2,
        '&:last-child': { pb: 2 },
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <IconComponent
        sx={{
          position: 'absolute',
          right: -4,
          bottom: -6,
          fontSize: 64,
          opacity: active ? 0.2 : 0.07,
          color: active ? 'primary.contrastText' : 'text.primary',
          transition: 'all var(--duration-normal) var(--easing-standard)',
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: active ? alpha(theme.palette.primary.contrastText, 0.85) : 'text.secondary',
          mb: 0.5,
          position: 'relative',
        }}
      >
        {card.label}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: active ? 'primary.contrastText' : (card.color ?? 'text.primary'),
          position: 'relative',
          letterSpacing: '-0.02em',
        }}
      >
        {loading ? '—' : Number(card.value || 0).toLocaleString()}
      </Typography>
    </CardContent>
  );

  return (
    <Card
      variant={active ? 'elevation' : 'outlined'}
      elevation={active ? 2 : 0}
      sx={{
        height: '100%',
        borderRadius: 'var(--radius-lg)',
        transition: 'all var(--duration-normal) var(--easing-standard)',
        ...(active
          ? {
              background: card.gradient,
              border: 'none',
            }
          : {
              bgcolor: 'background.paper',
            }),
      }}
    >
      <CardActionArea onClick={onClick} sx={{ height: '100%', borderRadius: 'var(--radius-lg)' }}>
        {cardContent}
      </CardActionArea>
    </Card>
  );
}

function EthernetStatsView(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const apiRef = useGridApiRef();

  // Data state
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, connected: 0, disconnected: 0 });
  const [loading, setLoading] = useState(false);

  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSiteGroup, setSelectedSiteGroup] = useState(null);
  const [connectionFilter, setConnectionFilter] = useState('all');

  // Auto-refresh state
  const [refreshInterval, setRefreshInterval] = useState(null);
  const intervalRef = useRef(null);

  // Kill dialog state
  const [killTarget, setKillTarget] = useState(null);
  const [killing, setKilling] = useState(false);
  const [duplicatesAnchorEl, setDuplicatesAnchorEl] = useState(null);
  const [duplicatesIdentifier, setDuplicatesIdentifier] = useState('');
  const [duplicatesRadios, setDuplicatesRadios] = useState([]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch data
  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (selectedCustomer) params.customer_id = selectedCustomer.id;
    if (selectedSiteGroup) params.site_group_id = selectedSiteGroup.id;

    fetchEthernetConnections(params)
      .then(data => {
        setRows(data.results || []);
        setSummary(data.summary || { total: 0, connected: 0, disconnected: 0 });
      })
      .finally(() => setLoading(false));
  }, [debouncedSearch, selectedCustomer, selectedSiteGroup]);

  // Initial fetch + refetch on filter change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh interval
  useEffect(() => {
    if (refreshInterval) {
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refreshInterval, fetchData]);

  // Derive unique customers and site groups from data for filter dropdowns
  const customerOptions = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      if (r.customer_id && !map.has(r.customer_id)) {
        map.set(r.customer_id, { id: r.customer_id, label: r.customer });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const siteGroupOptions = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      if (r.site_group_id && !map.has(r.site_group_id)) {
        if (!selectedCustomer || r.customer_id === selectedCustomer.id) {
          map.set(r.site_group_id, { id: r.site_group_id, label: r.site_group });
        }
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, selectedCustomer]);

  // Kill connection handler
  const handleKill = useCallback(() => {
    if (!killTarget) return;
    setKilling(true);
    killEthernetConnection(killTarget.identifier)
      .then(() => {
        setKillTarget(null);
        fetchData();
      })
      .finally(() => setKilling(false));
  }, [killTarget, fetchData]);

  const handleRefreshIntervalChange = (_event, newValue) => {
    setRefreshInterval(newValue);
  };

  const handleDuplicatesOpen = useCallback((event, row) => {
    event.stopPropagation();
    setDuplicatesAnchorEl(event.currentTarget);
    setDuplicatesIdentifier(row.identifier);
    setDuplicatesRadios(getMatchingRadios(row));
  }, []);

  const handleDuplicatesClose = useCallback(() => {
    setDuplicatesAnchorEl(null);
    setDuplicatesIdentifier('');
    setDuplicatesRadios([]);
  }, []);

  const handleConnectionFilterChange = useCallback(nextFilter => {
    setConnectionFilter(current => (current === nextFilter ? 'all' : nextFilter));
  }, []);

  const filteredRows = useMemo(() => {
    if (connectionFilter === 'connected') {
      return rows.filter(row => row.connected);
    }
    if (connectionFilter === 'disconnected') {
      return rows.filter(row => !row.connected);
    }
    return rows;
  }, [rows, connectionFilter]);

  const summaryCards = useMemo(
    () => [
      {
        label: t('ethernet.totalUnits'),
        value: summary.total,
        filter: 'all',
        gradient: 'var(--stat-gradient-info)',
        icon: DevicesIcon,
      },
      {
        label: t('ethernet.connected'),
        value: summary.connected,
        filter: 'connected',
        color: 'success.main',
        gradient: 'var(--stat-gradient-success)',
        icon: LinkIcon,
      },
      {
        label: t('ethernet.disconnected'),
        value: summary.disconnected,
        filter: 'disconnected',
        color: 'error.main',
        gradient: 'var(--stat-gradient-error)',
        icon: LinkOffIcon,
      },
    ],
    [summary, t],
  );

  // Columns
  const columns = useMemo(
    () => [
      {
        field: 'connected',
        headerName: t('general.status'),
        width: 110,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Typography variant="body2" fontWeight={600} color={params.value ? 'success.main' : 'text.disabled'}>
            {params.value ? t('ethernet.connected') : t('general.offline')}
          </Typography>
        ),
      },
      {
        field: 'identifier',
        headerName: t('ethernet.identifier'),
        minWidth: 220,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">{params.value}</Typography>
            {params.row.unregistered && <GridStatusCell label={t('ethernet.unregistered')} color="warning" />}
          </Box>
        ),
      },
      {
        field: 'duplicate_radios',
        headerName: t('ethernet.radios'),
        width: 110,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const radioCount = getRadioCountForIdentifier(params.row);
          if (radioCount == null) return '-';
          if (radioCount <= 1) return radioCount;
          return (
            <Tooltip title={t('ethernet.radiosShareIdentifier', { count: radioCount })}>
              <Chip
                label={radioCount}
                size="small"
                color="warning"
                variant="filled"
                clickable
                onClick={event => handleDuplicatesOpen(event, params.row)}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'customer',
        headerName: t('general.customer'),
        minWidth: 150,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'site_group',
        headerName: t('general.siteGroup'),
        minWidth: 150,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'site',
        headerName: t('general.site'),
        minWidth: 150,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'firmware_version',
        headerName: t('tables.firmware'),
        width: 120,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => params.value || params.row.version || '-',
      },
      {
        field: 'last_comm',
        headerName: t('ethernet.lastComm'),
        minWidth: 180,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => {
          if (!params.value) return '-';
          const d = new Date(params.value);
          return d.toLocaleString();
        },
      },
      {
        field: 'client_address',
        headerName: t('ethernet.clientAddress'),
        width: 180,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (value, row) => {
          if (row.client_ip) {
            return row.client_port ? `${row.client_ip}:${row.client_port}` : row.client_ip;
          }
          return null;
        },
        renderCell: params => params.value || '-',
      },
      {
        field: 'last_activity',
        headerName: t('ethernet.lastActivity'),
        minWidth: 180,
        flex: 1,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => {
          if (!params.value) return '-';
          const d = new Date(params.value);
          return d.toLocaleString();
        },
      },
      {
        field: 'queue',
        headerName: t('ethernet.daemon'),
        width: 140,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => params.value || '-',
      },
      {
        field: 'url',
        headerName: t('ethernet.link'),
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params =>
          params.value ? (
            <Link
              href={params.value}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'primary.main', '&:visited': { color: 'primary.main' } }}
            >
              {t('actions.view')}
            </Link>
          ) : null,
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params =>
          params.row.connected ? (
            <Tooltip title={t('ethernet.killConnection')}>
              <IconButton size="small" color="error" onClick={() => setKillTarget(params.row)}>
                <PowerSettingsNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null,
      },
    ],
    [handleDuplicatesOpen, t],
  );

  // Mobile list view column
  const listViewColumn = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: params => {
        const { row } = params;
        const duplicateRadioCount = getRadioCountForIdentifier(row);
        return (
          <Box sx={{ py: 1, px: 0.5, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <GridStatusCell
                label={row.connected ? t('ethernet.connected') : t('general.offline')}
                color={row.connected ? 'ok' : 'neutral'}
              />
              {row.unregistered && <GridStatusCell label={t('ethernet.unregistered')} color="warning" />}
              <Typography variant="body2" fontWeight={600}>
                {row.identifier}
              </Typography>
              {duplicateRadioCount > 1 && (
                <Tooltip title={t('ethernet.radiosShareIdentifier', { count: duplicateRadioCount })}>
                  <Chip
                    label={duplicateRadioCount}
                    size="small"
                    color="warning"
                    clickable
                    onClick={event => handleDuplicatesOpen(event, row)}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {[row.customer, row.site_group, row.site].filter(Boolean).join(' > ') || t('ethernet.noSystemAssignment')}
            </Typography>
            {(row.firmware_version || row.version) && (
              <Typography variant="caption" color="text.secondary" display="block">
                {t('ethernet.firmwareLabel', { value: row.firmware_version || row.version })}
              </Typography>
            )}
            {row.client_ip && (
              <Typography variant="caption" color="text.secondary" display="block">
                {t('ethernet.clientLabel', {
                  value: row.client_port ? `${row.client_ip}:${row.client_port}` : row.client_ip,
                })}
              </Typography>
            )}
            {row.last_activity && (
              <Typography variant="caption" color="text.secondary" display="block">
                {t('ethernet.activityLabel', { value: new Date(row.last_activity).toLocaleString() })}
              </Typography>
            )}
            {row.last_comm && (
              <Typography variant="caption" color="text.secondary" display="block">
                {t('ethernet.lastCommLabel', { value: new Date(row.last_comm).toLocaleString() })}
              </Typography>
            )}
          </Box>
        );
      },
    }),
    [handleDuplicatesOpen, t],
  );

  // Toolbar filter controls
  const filterControls = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          hiddenLabel
          placeholder={t('ethernet.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 200 }}
        />
        <Autocomplete
          size="small"
          options={customerOptions}
          getOptionLabel={o => o.label}
          value={selectedCustomer}
          onChange={(_e, v) => {
            setSelectedCustomer(v);
            setSelectedSiteGroup(null);
          }}
          renderInput={params => <TextField {...params} hiddenLabel placeholder={t('customer.customer')} />}
          sx={{ minWidth: 180 }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
        />
        <Autocomplete
          size="small"
          options={siteGroupOptions}
          getOptionLabel={o => o.label}
          value={selectedSiteGroup}
          onChange={(_e, v) => setSelectedSiteGroup(v)}
          renderInput={params => <TextField {...params} hiddenLabel placeholder={t('siteGroup.siteGroup')} />}
          sx={{ minWidth: 180 }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
        />
      </Box>
    ),
    [search, customerOptions, siteGroupOptions, selectedCustomer, selectedSiteGroup, t],
  );

  // Refresh controls
  const refreshControls = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <ToggleButtonGroup value={refreshInterval} exclusive onChange={handleRefreshIntervalChange} size="small">
          {REFRESH_OPTIONS.map(opt => (
            <ToggleButton key={opt.value} value={opt.value} sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title={t('actions.refresh')}>
          <IconButton size="small" onClick={fetchData}>
            {loading ? <CircularProgress size={20} /> : <SyncIcon />}
          </IconButton>
        </Tooltip>
      </Box>
    ),
    [refreshInterval, loading, fetchData, t],
  );

  // Export button
  const exportButton = useMemo(
    () => (
      <Tooltip title={t('ethernet.exportCsv')}>
        <span>
          <IconButton
            size="small"
            onClick={() => apiRef.current.exportDataAsCsv({ fileName: 'ethernet-connections' })}
            disabled={loading}
          >
            <FileDownload fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [loading, apiRef, t],
  );

  return (
    <AppNav {...props} loading={loading} page="admin_ethernet" notFullHeight>
      <Box
        sx={{
          top: '64px',
          mx: 'var(--page-px)',
          pb: 'var(--page-py)',
          height: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TitleBar title={t('pages.ethernetDashboard')} showBackButton />
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 2, mb: 2 }}
        >
          {summaryCards.map(card => (
            <SummaryCard
              key={card.label}
              card={card}
              loading={loading}
              active={connectionFilter === card.filter}
              onClick={() => handleConnectionFilterChange(card.filter)}
            />
          ))}
        </Box>
        <DataGridToolbar
          apiRef={apiRef}
          showQuickFilter={false}
          toolbarContent={filterControls}
          toolbarActions={
            <>
              {refreshControls}
              {exportButton}
            </>
          }
        />
        <DataGridPro
          apiRef={apiRef}
          rows={filteredRows}
          columns={columns}
          getRowId={row => row.radio_id ?? `unregistered_${row.identifier}`}
          loading={loading}
          getRowHeight={() => 'auto'}
          listView={isMobile}
          listViewColumn={listViewColumn}
          disableColumnFilter
          disableColumnSelector
          disableRowSelectionOnClick
          density="compact"
          hideFooter
          sx={{ flex: 1, minHeight: 0 }}
        />
      </Box>
      <Popover
        open={Boolean(duplicatesAnchorEl)}
        anchorEl={duplicatesAnchorEl}
        onClose={handleDuplicatesClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, minWidth: 280, maxWidth: 360 }}>
          <Typography variant="subtitle2" sx={{ px: 1, pb: 0.5 }}>
            {t('ethernet.radiosFor', { identifier: duplicatesIdentifier })}
          </Typography>
          <List dense sx={{ py: 0 }}>
            {duplicatesRadios.map(radio => (
              <ListItemButton
                key={radio.id}
                component="a"
                href={`/support/radios/${radio.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', '&:visited': { color: 'primary.main' } }}
              >
                <ListItemText
                  primary={radio.label}
                  secondary={
                    radio.siteUrl
                      ? `${`/support/radios/${radio.id}`} | ${radio.siteName || radio.siteUrl}`
                      : `/support/radios/${radio.id}`
                  }
                />
              </ListItemButton>
            ))}
          </List>
          {duplicatesRadios.some(radio => radio.siteUrl) && (
            <Box sx={{ px: 1, pt: 0.5 }}>
              {duplicatesRadios.map(radio =>
                radio.siteUrl ? (
                  <Typography key={`site-link-${radio.id}`} variant="caption" display="block">
                    {t('ethernet.siteLabel', { label: radio.label })}{' '}
                    <Link
                      href={radio.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: 'primary.main', '&:visited': { color: 'primary.main' } }}
                    >
                      {radio.siteName || radio.siteUrl}
                    </Link>
                  </Typography>
                ) : null,
              )}
            </Box>
          )}
        </Box>
      </Popover>
      <Dialog open={Boolean(killTarget)} onClose={() => !killing && setKillTarget(null)}>
        <DialogTitle>{t('ethernet.killConnection')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ethernet.killConnectionConfirm', { identifier: killTarget?.identifier })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillTarget(null)} disabled={killing}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={handleKill} color="error" variant="contained" disabled={killing}>
            {killing ? <CircularProgress size={20} /> : t('ethernet.kill')}
          </Button>
        </DialogActions>
      </Dialog>
    </AppNav>
  );
}

export default EthernetStatsView;
